import type { ProductionNoteSource } from "@pufimi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { OrderDetailView } from "@/components/orders/order-detail";
import { adminOrderQueryKey } from "@/lib/admin-queries";
import { AdminApiError, adminApi } from "@/lib/api";

// Trigger a browser save of the fetched zip. Building an object URL from the Blob
// (rather than linking straight to the endpoint) keeps the admin session cookie
// on the fetch and works cross-origin — the API lives on a different host in prod.
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function OrderDetailRoute() {
  const { orderId } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: adminOrderQueryKey(orderId),
    queryFn: () => adminApi.orders.get(orderId),
  });

  // Tick/un-tick returns the re-projected detail; drop it straight into the
  // cache so the status badge, checklist, and timeline all update in one shot
  // (no extra refetch). The checklist row does the optimistic move + rollback.
  const stepMutation = useMutation({
    mutationFn: (vars: {
      step: string;
      action: "tick" | "untick";
      orderLineId?: string;
      trackingNumber?: string;
      trackingCarrier?: string;
    }) => adminApi.orders.applyStep(orderId, vars),
    onSuccess: (detail) => queryClient.setQueryData(adminOrderQueryKey(orderId), detail),
  });

  // Adding a note returns the re-projected detail (same as a step); drop it into
  // the cache so the notes thread grows in one shot. The compose box clears once
  // this resolves (#244).
  const noteMutation = useMutation({
    mutationFn: (text: string) => adminApi.orders.addNote(orderId, { text }),
    onSuccess: (detail) => queryClient.setQueryData(adminOrderQueryKey(orderId), detail),
  });

  // Generating the producer packet (#282/#283) returns the re-projected detail —
  // the file grid appears AND the `production_files_generated` checklist step
  // lights up in one cache write, no refetch.
  const generateFilesMutation = useMutation({
    mutationFn: () => adminApi.orders.generateProductionFiles(orderId),
    onSuccess: (detail) => queryClient.setQueryData(adminOrderQueryKey(orderId), detail),
  });

  // Adding a producer note returns the re-projected detail (same as an internal
  // note); drop it into the cache so the producer-notes thread grows in one shot.
  const productionNoteMutation = useMutation({
    mutationFn: (input: { source: ProductionNoteSource; body: string }) =>
      adminApi.orders.addProductionNote(orderId, input),
    onSuccess: (detail) => queryClient.setQueryData(adminOrderQueryKey(orderId), detail),
  });

  const isNotFound = query.error instanceof AdminApiError && query.error.status === 404;

  return (
    <div className="space-y-6">
      <Link
        to="/orders"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Wszystkie zamówienia
      </Link>

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          {isNotFound ? "Nie znaleziono zamówienia." : "Nie udało się wczytać zamówienia."}
        </p>
      ) : (
        <OrderDetailView
          detail={query.data}
          onToggleStep={async (step, action, tracking) => {
            await stepMutation.mutateAsync({
              step,
              action,
              trackingNumber: tracking?.number,
              // Persist an explicit null carrier as omitted (schema treats it optional).
              trackingCarrier: tracking?.carrier ?? undefined,
            });
          }}
          onToggleLineStep={async (orderLineId, step, action) => {
            // A per-line production step (#408) tags the appended event with its line.
            await stepMutation.mutateAsync({ step, action, orderLineId });
          }}
          onAddNote={async (text) => {
            await noteMutation.mutateAsync(text);
          }}
          onGenerateProductionFiles={async () => {
            await generateFilesMutation.mutateAsync();
          }}
          onExportProductionFiles={async () => {
            const blob = await adminApi.orders.exportProductionFilesZip(orderId);
            saveBlob(blob, `pufimi-order-${orderId}.zip`);
          }}
          onAddProductionNote={async (input) => {
            await productionNoteMutation.mutateAsync(input);
          }}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/orders/$orderId")({ component: OrderDetailRoute });
